import { authLogger as logger } from '../utils/logger';
import axios from 'axios';

// Create a dedicated axios instance for auth operations
const authAPI = axios.create({
  baseURL: '/api',  // Use relative URL to work with the proxy in development
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add request interceptor for debugging
authAPI.interceptors.request.use(
  config => {
    logger.debug(`AUTH API Request: ${config.method.toUpperCase()} ${config.baseURL}${config.url}`);
    logger.debug('Request headers:', config.headers);
    logger.debug('Request data:', config.data);
    return config;
  },
  error => {
    logger.error('Auth API request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
authAPI.interceptors.response.use(
  response => {
    logger.debug(`AUTH API Response from ${response.config.url}:`, response.status, response.statusText);
    logger.debug('Response data:', response.data);
    return response;
  },
  error => {
    logger.error('Auth API response error:',
      error.response?.status,
      error.response?.statusText,
      error.response?.data);
    return Promise.reject(error);
  }
);

export const authService = {
  login: async (email, password) => {
    try {
      logger.debug('Attempting login with:', { email });
      const response = await authAPI.post('/auth/login', { email, password });
      return response; // Return full response object, not just response.data
    } catch (error) {
      logger.error('Login error:', error.response?.data || error.message);
      throw error;
    }
  },

  signup: async (email, password, fullName) => {
    try {
      logger.debug('Attempting signup with:', { email, fullName });
      const response = await authAPI.post('/auth/signup', { email, password, fullName });
      return response; // Return full response object, not just response.data
    } catch (error) {
      logger.error('Signup error:', error.response?.data || error.message);
      throw error;
    }
  },

  logout: async () => {
    try {
      // Call server logout endpoint to clear httpOnly cookies
      await authAPI.post('/auth/logout');
      logger.debug('Logout successful, cookies cleared');
    } catch (error) {
      logger.error('Logout error:', error);
      // Continue with client-side cleanup even if server call fails
    }
    // Clear any client-side user data
    localStorage.removeItem('user');
  },

  getCurrentUser: async () => {
    try {
      // Fetch current user info from server using httpOnly cookie
      const response = await authAPI.get('/auth/me');
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      logger.error('Error fetching current user:', error);
      return null;
    }
  },

  // Email verification methods
  verifyEmail: async (token) => {
    try {
      logger.debug('Verifying email with token:', token);
      const response = await authAPI.post(`/auth/verify-email?token=${token}`);
      return response.data;
    } catch (error) {
      logger.error('Email verification error:', error.response?.data || error.message);
      throw error;
    }
  },

  resendVerification: async (email) => {
    try {
      logger.debug('Resending verification email to:', email);
      const response = await authAPI.post(`/auth/resend-verification?email=${email}`);
      return response.data;
    } catch (error) {
      logger.error('Resend verification error:', error.response?.data || error.message);
      throw error;
    }
  },

  forgotPassword: async (email) => {
    try {
      logger.debug('Requesting password reset for:', email);
      const response = await authAPI.post(`/auth/forgot-password?email=${email}`);
      return response.data;
    } catch (error) {
      logger.error('Forgot password error:', error.response?.data || error.message);
      throw error;
    }
  },

  resetPassword: async (token, newPassword) => {
    try {
      logger.debug('Resetting password with token:', token);
      const response = await authAPI.post('/auth/reset-password', {
        token: token,
        newPassword: newPassword
      });
      return response.data;
    } catch (error) {
      logger.error('Reset password error:', error.response?.data || error.message);
      throw error;
    }
  },

  refreshToken: async () => {
    try {
      logger.debug('Attempting to refresh token...');
      const response = await authAPI.post('/auth/refresh');
      if (response.data.success) {
        logger.debug('Token refresh successful');
        return response.data;
      }
      throw new Error('Token refresh failed');
    } catch (error) {
      logger.error('Token refresh error:', error.response?.data || error.message);
      throw error;
    }
  }
};

export default authService;
